'use strict'

import { gsap, TimelineMax, TweenMax } from 'gsap';
import { CSSRulePlugin } from 'gsap/dist/CSSRulePlugin';
import { ScrollToPlugin } from 'gsap/dist/ScrollToPlugin';
gsap.registerPlugin(ScrollToPlugin, CSSRulePlugin);
import TimelineModule from '../modules/timeline';
import customCursors from './custom-cursors';
import Share from '../modules/share';
import {
  isDesktop, 
  getScrollbarWidth,
  toggleScrollState, 
  enableScroll, 
  disableScroll, 
  isModalOpen, 
  getMarkupFromBackend,
  scrollEnabled,
  scrollToTop,
  BOOTSTRAP_BREAKPOINT_EXTRA_LARGE,
  BOOTSTRAP_BREAKPOINT_LARGE,
  BOOTSTRAP_BREAKPOINT_MEDIUM,
  BREAKPOINT_HEIGHT_SMALL,
  BREAKPOINT_HEIGHT_LARGE,
  BREAKPOINT_HEIGHT_EXTRA_LARGE,
  CLASS_BOOTSTRAP_OVERFLOW_HIDDEN,
  CLASS_BOOTSTRAP_CENTER_CONTENT,
  LOCAL_STORAGE_KEY_SMALLEST_HEIGHT,
  CONTAINER_MAX_WIDTH,
  isTouchScreen,
  isDevice,
  isBrowser,
  convertVhToPixels,
  setVhValue,
  BREAKPOINT_HEIGHT_MEDIUM,
  CLASS_ACTIVE,
  calculateContainerPositionFromSide,
  setDocumentTitle,
  setMetaContent,
  setOgProperty,
} from '../modules/helpers';

// GSAP timelines (https://greensock.com/docs/v3/GSAP/Timeline)
// On page load animations
let timelineEntrance;
// Years scrolling animations
let timeline;
// Movement to year page and back
let timelineSelectYear;
// SAMPOREP-35 Holds year page popstate listener
let yearPagePopstateListener;

// For requestAnimationFrame call on scroll event
let requestId = null;

// Tracks touch movement on touch events
let xDown = null;                                                        
let yDown = null;

// Event names
const mouseWheelEvent = 'onwheel' in document ? 'wheel' : 'onmousewheel' in document ? 'mousewheel' : 'DOMMouseScroll';
const scrollEvent = 'scroll';

// Resize variables
// With this we check last resize
let resizeTimer; 
// Animation position before resize (set in first resize)
const firstResizeData = {}
// Checks if timeline's reconstruction is in progress (don't start reconstruction if it's already calculating)
let calculateDimensionsInProgress = false;

// Scroll position where hero disappears on mobile
const scrollYInMobileWhereHeroDisappears = 10;

/**
 * Module for main page of annual report archive. Functionality for timeline and year page.
 * 
 * There are 3 different categories for functions
 *  - Helpers - 
 *  - Javascript event functions
 *  - Main functions
 */
const yearScroller = {

  // If page was loaded with year parameter, it is stored to this variable
  onLoadYear: false,
  // Gets document title (used when page changes between timeline and year page)
  timelinePageTitle: document.title,

  ////////////////////////////////////////////////////////
  /// Helpers
  ////////////////////////////////////////////////////////

  /**
   * Centers header and footer. This is needed because page has max width for container which was added late in the project and broke centering.
   * Transform attributes was used after container width but it causes flickering in new Safari 13. Solution is to use pixels for positioning.
   */
  centerHeaders: function () {

    const positionFromLeft = window.innerWidth > CONTAINER_MAX_WIDTH ? (window.innerWidth - CONTAINER_MAX_WIDTH) / 2 : 0;
    const header = document.querySelector('.body-header');
    const footer = document.querySelector('.body-footer');
    header.style.left = `${positionFromLeft}px`;
    footer.style.left = `${positionFromLeft}px`;
  },

  /**
   * Gets year scroller element
   * 
   * @returns {Object} Year scroller element. 
   */
  getScroller: function () {
    return document.querySelector('.year-scroller');
  },

  /**
   * Gets button element by year number
   *
   * @param {number} year Year number
   * @returns {Object} Year button element 
   */
  getYearButton: function (year) {
    return document.querySelector(`.year-scroller .years .year-has-annual-report[data-year="${year}"] button`);
  },

  /**
   * Gets all year button elements
   * 
   * @returns {Object} year button element 
   */
  getYearButtons: function () {
    return document.querySelectorAll('.year-scroller .years .year-has-annual-report button');
  },

  /**
   * Gets year scroller block element by year number
   * 
   * @param {number} year Year number
   * @returns {Object} Year scroller block
   */
  getYearScrollerBlock: function (year) {
    return document.querySelector(`.year-scroller-block[data-year="${year}"]`);
  },
  
  /**
   * Gets CSS selector for given year content block. Year content block holds fetched year data.
   * 
   * @param {number} year Year number
   * @returns {string} CSS selector for year content element
   */
  getYearContentSelector: function (year) {
    return `.year-has-annual-report[data-year="${year}"] .year-content`;
  },

  /**
   * Updates year scroller position on scroll event.
   */
  update: function () {
    const topPosition = window.pageYOffset;
    timeline.time(topPosition);
    requestId = null;
  },

  /**
   * Checks if year scroller element exists.
   * 
   * @returns {boolean} True if year scroller element exists and false if doesn't exists
   */
  exists: function () {
    return typeof document.querySelector('.year-scroller') !== 'undefined' && document.querySelector('.year-scroller') !== null;
  },

  /**
   * Inits on load year if page was loaded with year parameter.
   */
  initOnLoadYear: function () {
    this.onLoadYear = document.querySelector('.year-scroller').dataset.onLoadYear;
  },

  /**
   * Checks if year page is visible.
   * 
   * @returns {boolean} True if year page is visible and false if not (if false timeline is visible or page is animating movement between timeline and year page)
   */
  yearPageVisible: function() {
    return (typeof timelineSelectYear !== 'undefined' && timelineSelectYear && timelineSelectYear.progress() > 0);
  },

  /**
   * Moves year scroller to next position based on direction. Check function code and comments for specific movement.
   * Basically this is used only in desktop which has scroll hijack. In mobile there are normal scrolling.
   * 
   * @param {boolean} directionUp True if scroller should be moved up one step. If false scroller moves down to the next step.
   */
  moveYearScroll: function(directionUp) {

    if (isDesktop() && scrollEnabled() && !isModalOpen() && !this.yearPageVisible()) {

      // Entrance animations are not executed
      if (timelineEntrance.progress() < 1) {

        if (!directionUp) {
          disableScroll();
          timelineEntrance.play();
        }

      // Entrance animations are executed
      } else {

        // Variables to define next movement
        const nextLabel = directionUp ? timeline.previousLabel() : timeline.nextLabel();

        // Move to hero visible position
        if (directionUp && typeof nextLabel === 'undefined') {
          toggleScrollState();
          Array.from(TimelineModule.getAllTimelines()).map(element => {
            TimelineModule.toggleInteractableClass(element);
            const textWrappers = document.querySelectorAll(`#${element.id} .text-wrappers`);
            Array.from(textWrappers).map(textWrapper => textWrapper.style.opacity = 0);
            customCursors.removeElementCursorType(element);
          });

          // Remove cursor from first year
          customCursors.removeElementCursorType(document.querySelector('.year-button[data-custom-cursor="year"]'));

          // Make sure that no years are visible (hover state of timeline year) when entrance animation is reversed
          Array.from(document.querySelectorAll('.timeline .year:not(.highlight) .text-wrapper')).map(item => item.style.opacity = null);
          Array.from(document.querySelectorAll('.timeline .year:not(.highlight) .marker-wrapper')).map(item => item.style.width = null);
          timelineEntrance.reverse();

        // Move to next year
        } else if (typeof nextLabel !== 'undefined') {
          this.scrollToLabel(nextLabel);
        }
        
      }
    }
  },

  /**
   * Scrolls year scroller to specific label. Labels are years.
   * 
   * @param {string} label Label of year scroller.
   */
  scrollToLabel: function(
    label
  ) {
    if (scrollEnabled()) {

      disableScroll();

      const onCompleteFunction = (scrollPosition) => {
        // In some browsers scroll y position is not rounded to integer after scrolling to label although scroll position is integer.
        // This call ensures that timeline is in scroll y position after scrolling is finished.
        setTimeout(() => {
          window.scrollTo(0,scrollPosition);
          timeline.time(scrollPosition);
          enableScroll();
        },100)
      }

      // Jira SAMPOTEST-48: changed animation faster in medium-large (=iPad landscape) sized devices.
      let duration = 0.6;
      if (window.innerWidth < BOOTSTRAP_BREAKPOINT_LARGE) {
        duration = 0.4;
      }

      const scrollPositionAfterAnimation = timeline.labels[label];
      gsap.to(
        window, {
          duration: duration,
          ease: 'power1.inOut',
          scrollTo: scrollPositionAfterAnimation,
          onComplete: onCompleteFunction,
          onCompleteParams: [scrollPositionAfterAnimation]
        }
      )
    }
  },

  /*hideShades: function () {
    Array.from(document.querySelectorAll('.background.shade')).map(element => element.classList.add('d-none'));
  },

  showShades: function () {
    Array.from(document.querySelectorAll('.background.shade')).map(element => element.classList.remove('d-none'));
  },*/

  /**
   * Removes main timeline, creates it again and scrolls back to previous position.
   */
  reinitMainTimeline: function () {
    const currentLabel = timeline.currentLabel();
    this.clearGivenTimeline(timeline);
    this.createTimeline();
    window.scrollTo(0,timeline.labels[currentLabel]);
  },

  /**
   * Updates language links. For example when moved to year page from timeline we should update language links to match current year page.
   * 
   * @param {string} suffix Suffix for language url (for example year number)
   */
  updateLanguageLinks: function (suffix = '') {
    const languageLinks = document.querySelectorAll('.languages li a');
    Array.from(languageLinks).forEach((item,index) => {
      const originalHref = item.dataset.hrefOriginal;
      item.href = `${originalHref}${suffix}`;
    });
  },

  /**
   * SAMPOTEST-80: 
   * Sets height of tabs element so year page content doesn't jump 
   * when height of tab element changes.
   * 
   * @param {Object} tabContentElement - Year page tab element
   * @param {number} tabContentElementVerticalMovement - Tab content movement in animation. 
   *                                                   With this we can calculate height of tab content before animation renders.
   */
  setMaxHeightOfTabContent: function (tabContentElement,tabContentElementVerticalMovement = 0) {
    const heights = [];
    const tabPanes = tabContentElement.children;
    if (tabPanes.length > 0) {
      for (let i = 0; i < tabPanes.length; i++) {
        const tabPane = tabPanes[i];
        let removeActiveClass = false;
        if (!tabPane.classList.contains(CLASS_ACTIVE)){
          tabPane.classList.add(CLASS_ACTIVE);
          removeActiveClass = true;
        }
        heights.push(tabPane.offsetHeight);
        if (removeActiveClass) {
          tabPane.classList.remove(CLASS_ACTIVE);
        }
      }
    }
    const maxHeight = Math.max(...heights) + tabContentElementVerticalMovement;
    tabContentElement.style.height = `${maxHeight}px`;
  },

  ////////////////////////////////////////////////////////
  /// Javascript event functions
  ////////////////////////////////////////////////////////

  /**
   * Functionality for key down events.
   * 
   * @param {Object} event onKeydown event object
   */
  onKeydown: function (event) {
    if (isDesktop() && !this.yearPageVisible()) {  
      const e = event || window.event;

      // Up arrow
      if (e.keyCode === 38) {

        // Move year scroller up
        this.moveYearScroll(true);

      // Down arrow
      } else if (e.keyCode === 40) {

        // Move year scroller down
        this.moveYearScroll(false);
      }
    }
  },

  /**
   * When timeline is clicked it moves to clicked year on timeline page. 
   * When clicked timeline year on year page clicked year page is loaded.
   * 
   * @param {Array} timelineYears Year elements of timelines.
   */
  createOnClickEventsForTimelineYears: function(timelineYears) {
    // Loop timeline years
    timelineYears.forEach((timelineYearElement,index) => {
      const year = timelineYearElement.dataset.year;
      const timelineElement = timelineYearElement.parentElement;

      if (!timelineYearElement.classList.contains(TimelineModule.dom.classes.NOT_INTERACTABLE)) {
        // Initialize click function
        timelineYearElement.onclick = () => {
          if (
              // In desktop interactable class is also needed
              (isDesktop() && timelineElement.classList.contains(TimelineModule.dom.classes.INTERACTABLE) || !isDesktop())
            ) {
            if (!this.yearPageVisible()) {
              this.scrollToLabel(year);
            } else {
              window.location.href = timelineYearElement.dataset.href;
            }
          }
        };
      }
    });
  },  

  /**
   * Registers click event for given year buttons on year scroller.
   * Click of year button starts moves to year page.
   * 
   * @param {Array} buttons Year scroller year button elements.
   */
  createOnClickEventForScrollerYearButtons: function(buttons) {

    buttons.forEach((buttonElement,index) => {

      // Initialize click function
      buttonElement.onclick = (event) => {
        const year = buttonElement.dataset.year;
        const timelinePageHref = buttonElement.dataset.timelinePageHref;
        const yearPageName = buttonElement.dataset.yearPageName;
        const inDesktopClickedActiveYear = (isDesktop() && year === timeline.currentLabel());

        if (
          (inDesktopClickedActiveYear || !isDesktop()) // Only active year can be clicked
          && scrollEnabled() // Scroll must be enabled (= no transitions are in progress)
          && (!timelineSelectYear || (timelineSelectYear && timelineSelectYear.progress() === 0)) // In year page we cannot click year button
          && (!isDesktop() || (!timelineEntrance || timelineEntrance.progress() === 1)) // Timeline entrance should be finished before first year can be clicked.
        ){  
          // Deny scrolling in mobile before year page opens.
          if (!isDesktop()) {
            document.body.classList.add(CLASS_BOOTSTRAP_OVERFLOW_HIDDEN);
          }
          disableScroll();
          const url = `${timelinePageHref}${yearPageName}/${year}/`;
          history.pushState({}, year, url);
          this.showYearContent(year);

          // SAMPOREP-35 Add popstate listener for back button when year page is shown
          window.addEventListener('popstate', yearPagePopstateListener);
        }
      };
    });
  },
  
  /**
   * Year page back button click functionality on desktop view.
   */
  desktopBackButtonClick: function() {
    const year = document.querySelector('.year-scroller').getAttribute('data-active-year-page');
    document.querySelector('.year-scroller').classList.remove('year-page-active');
    const yearScrollerBlock = document.querySelector(`.year-scroller-block[data-year="${year}"]`);
    if (yearScrollerBlock !== null) {
      yearScrollerBlock.classList.remove('active');
      yearScrollerBlock.style.top = 'auto';
    }
    document.querySelector('.page-default').classList.remove(CLASS_BOOTSTRAP_OVERFLOW_HIDDEN);
    document.querySelector('.page-default').style.height = 'auto';
    window.scrollTo(0,timeline.labels[year]);
  },

  /**
   * Back button click
   * 
   * @param {String} backHref Next url.
   */
  backButtonClick: function(backHref) {
    if (isDesktop()) {
      this.desktopBackButtonClick();
    }
    timelineSelectYear.reverse();
    this.updatePageData(backHref, window.LOCALIZATIONS.TIMELINE_PAGE_TITLE, window.LOCALIZATIONS.SHARE_TITLE);
    history.pushState({}, window.LOCALIZATIONS.SHARE_TITLE, backHref);

    // SAMPOREP-35 Remove popstate listener for back button if it exists
    window.removeEventListener('popstate', yearPagePopstateListener);
  },

  /**
   * Registers click events for back buttons. Mobile and desktop has different back buttons
   * and those has also different functionality.
   * 
   * @param {Array} buttons Back button elements.
   */
  createOnClickEventForBackButton: function(buttons) {
    Array.from(buttons).map(button => {
      const backHref = button.dataset.href;
      button.onclick = () => this.backButtonClick(backHref);
    });
  },

  /**
   * Updates page data for example data
   * 
   * @param {string} url New page url
   * @param {string} title Page title
   * @param {string} description Page's meta description text
   */
  updatePageData: function (url, title, description) {
    setDocumentTitle(title);
    setOgProperty('title', title);
    setOgProperty('description', description);
    setMetaContent('description', description);
    setMetaContent('twitter:description', description);
    setMetaContent('twitter:title', title);
    Share.setLinkToBeCopiedToClipboard(url);
    Share.setFacebookShareUrl(url);
    Share.setLinkedInShareUrl(url, title, title);
    Share.setTwitterShareUrl(url, title);
    this.updateLanguageLinks();
  },

  /**
   * Registers click event for hero's scroll down button.
   * 
   * @param {Object} button Scroll down button on hero
   */
  createOnClickEventForHeroScrollDownButton: function(button) {
    button.onclick = (event) => {
      // Play entrance animations to the end
      timelineEntrance.play();
    }
  },

  /**
   * Scroll event functionality. This updates position of main timeline on scroll event.
   */
  scrollEventFunction: function () {

    //tempBodyHeight = document.body.innerHeight;

    // Updates entrance animation on mobile.
    if (!isDesktop() && (timelineEntrance.paused() || timelineEntrance.progress() === 1) && !this.yearPageVisible()) {

      // If timeline entrance is not finished it should be played to the end on scroll
      if (window.scrollY >= scrollYInMobileWhereHeroDisappears && timelineEntrance.progress() < 1) {
        timelineEntrance.play();

      // If timeline entrance animations is completed and user scrolls back to top show hero again
      } else if (window.scrollY < scrollYInMobileWhereHeroDisappears && timelineEntrance.progress() === 1) {
        timelineEntrance.reverse();
      }
    }

    // Updates timeline position
    if (!requestId && !this.yearPageVisible()) {
      requestId = requestAnimationFrame(this.update);
    }
  },

  /**
   * Reconstructs timelines based on current view (desktop or mobile). Animations are strongly
   * related on current size of animated elements. That's why this functions is used on resize event.
   */
  calculateDimensions: function () {
    calculateDimensionsInProgress = true;

    // Check if we should re-center headers
    this.centerHeaders();

    // Check if select year on timeline animations are going on (= year just clicked or back button just clicked). Then it's need to be reverted
    if (typeof timelineSelectYear !== 'undefined' && timelineSelectYear.progress() > 0 && timelineSelectYear.progress() < 1) {
      timelineSelectYear.progress(1);
    }

    // Reverts timeline changes to DOM and it's position
    if (isDesktop()) {

      // Select year
      if (typeof timelineSelectYear !== 'undefined') {

        document.querySelector('.year-scroller').classList.remove('year-page-active');
        const year = document.querySelector('.year-scroller').getAttribute('data-active-year-page');
        if (year !== null) {
          const yearScrollerBlock = document.querySelector(`.year-scroller-block[data-year="${year}"]`);
          yearScrollerBlock.classList.remove('active');
          yearScrollerBlock.style.top = 'auto';
        }
        document.querySelector('.page-default').classList.remove(CLASS_BOOTSTRAP_OVERFLOW_HIDDEN);
        document.querySelector('.page-default').style.height = 'auto';
        window.scrollTo(0,timeline.labels[year]);

        timelineSelectYear.progress(0);

        this.clearGivenTimeline(timelineSelectYear);
        this.getScroller().removeAttribute('data-active-year-page');
      }

      // Turn interactable class off from timelines.
      Array.from(TimelineModule.getAllTimelines()).map(element => element.classList.remove(TimelineModule.dom.classes.INTERACTABLE));
      this.clearGivenTimeline(timelineEntrance);
      this.createTimelineEntrance();
      timelineEntrance.progress(1);
      
      // Main timeline
      timeline.progress(0);
      this.clearGivenTimeline(timeline);
      this.createTimeline();

      // Scroll timeline to correct position before resize
      window.scrollTo(0,timeline.labels[firstResizeData.activeYearOnTimeline]);

      // Was it year page before resize
      if (firstResizeData.activeYearPage !== null && firstResizeData.yearPageVisible) {
        // Move to year page without animations
        this.showYearContent(firstResizeData.activeYearPage,false);
      }

      TimelineModule.setPositionOfTimelines();

    } else {

      // Only when previous view was desktop. Otherwise do nothing.
      if (firstResizeData.isDesktop) {
        this.switchDesktopToMobile(firstResizeData);
      }
    }

    this.update();

    calculateDimensionsInProgress = false;

  },

  /**
   * Resize event function. Saves animation positions on first resize. 
   * When resize is stopped animation timelines are recreated and moved to position on first resize (check calculateDimensions function).
   */
  resizeEventFunction: function () {
    if (!calculateDimensionsInProgress) {
      if (resizeTimer) {
        clearTimeout(resizeTimer);   // clear any previous pending timer
      } else {
        firstResizeData.isDesktop = isDesktop();
        firstResizeData.yearPageVisible = this.yearPageVisible();
        const activeYearPage = this.getScroller().getAttribute('data-active-year-page');
        firstResizeData.activeYearOnTimeline = (activeYearPage === null) ? timeline.currentLabel() : activeYearPage;
        firstResizeData.activeYearPage = activeYearPage;
      }

      const calculateDimensions = this.calculateDimensions.bind(this);

      // set new timer
      resizeTimer = setTimeout(() => {
        
        resizeTimer = null;
        calculateDimensions();
        
      }, 200); // Delay before starts calculating
    }
  },

  /**
   * Touch move event functionality on touch screens.
   * Based on touch move it moves year scroller to the next step.
   * Calculates movement based on touch start coordinates (check handleTouchStart function).
   * 
   * @param {Object} event Javascript event of touchMove.
   */
  handleTouchMove: function (event) {
    if ( ! xDown || ! yDown ) {
      return;
    }

    const xUp = event.touches[0].clientX;                                    
    const yUp = event.touches[0].clientY;

    const xDiff = xDown - xUp;
    const yDiff = yDown - yUp;

    // Y scroll is bigger than X
    if ( Math.abs( xDiff ) < Math.abs( yDiff ) ) {
      // Up swipe
      const isUpSwipe = yDiff > 0 ? false : true;
      this.moveYearScroll(isUpSwipe);                                                            
    }

    xDown = null;
    yDown = null;                      
  },

  /**
   * Touch start event for touch screens. Saves first touch coordinates.
   * 
   * @param {Object} event Javascript event of touchStart
   */
  handleTouchStart: function (event) {
    const firstTouch = this.getTouches(event)[0];                                      
    xDown = firstTouch.clientX;                                      
    yDown = firstTouch.clientY;                                      
  },

  /**
   * Gets touches object from given element.
   * 
   * @param {Object} event Javascript touch event. 
   * @returns {Object} Touches object from event data
   */
  getTouches: function (event) {
    return event.touches ||             // browser API
        event.originalEvent.touches; // jQuery
  },

  /**
   * Functionality which is executed on mouse scroll.
   */
  mouseWheelEventFunction: function () {

    // Calculate scroll movement
    let normalized;
    if (event.wheelDelta) {
      normalized = (event.wheelDelta % 120 - 0) === -0 ? event.wheelDelta / 120 : event.wheelDelta / 12;
    } else {
      var rawAmmount = event.deltaY ? event.deltaY : event.detail;
      normalized = -(rawAmmount % 3 ? rawAmmount * 10 : rawAmmount / 3);
    }

    // Variables to define next movement
    const isUpScroll = normalized > 0 ? true : false;

    this.moveYearScroll(isUpScroll);
  },

  ////////////////////////////////////////////////////////
  /// Main functions
  ////////////////////////////////////////////////////////

  /**
   * Moves to year page from timeline. First it fetches year markup from Stage. On done event function it
   * creates animation timeline for given year. After that it starts animations and moves to year page.
   * If animate is false it totally skips created animations (useful on resize event for example).
   * This animation timeline is reversed on back button click. Function creates different transition
   * for desktop and mobile (on mobile there are no animation but it could be added in the future).
   * 
   * @param {string} year Year number 
   * @param {Boolean} [animate=true] Parameter which decides if it animates movement to year page.
   */
  showYearContent: function(year, animate = true) {        

    const yearButton = this.getYearButton(year);
    const yearScrollerBlock = yearButton.parentElement;
    const yearScrollerYearElement = yearButton.firstElementChild;
    const yearContentElementSelector = this.getYearContentSelector(year);

    const calculateYearNumberPixelsFromRight = (year) => {
      const yearButton = this.getYearButton(year);
      const yearScrollerYearElement = yearButton.firstElementChild;
      const pixelsFromTheHorizontalCenter = 60;
      const widthMultiplier = window.innerWidth >= BOOTSTRAP_BREAKPOINT_LARGE ? 0.5 : 0.66;
      const pixelsFromRight = pixelsFromTheHorizontalCenter + (yearScrollerYearElement.parentElement.parentElement.offsetWidth * widthMultiplier - (yearScrollerYearElement.parentElement.parentElement.offsetWidth - yearScrollerYearElement.offsetWidth)/2);
      return pixelsFromRight;
    };
    const yearNumberPixelsFromRight = calculateYearNumberPixelsFromRight(year);

    // This is done after ajax call.
    const onDone = (data) => {

      // Enable scroll after ajax call if desktop
      if (isDesktop()) {
        enableScroll();
      }

      // Wrapper for year content in timeline year
      const yearContentElement = document.querySelector(yearContentElementSelector);
      yearContentElement.innerHTML = data;

      // Main wrapper element inside year content wrapper which contains for example data attributes
      const yearPageWrapper = yearContentElement.firstElementChild;
      yearPageWrapper.style.left = `${calculateContainerPositionFromSide()}px`;

      // Update data attributes
      const title = yearPageWrapper.dataset.title;
      const description = yearPageWrapper.dataset.description;
      this.updatePageData(window.location.href, title, description);
      this.getScroller().setAttribute('data-active-year-page',year);

      // Update language links for this year
      this.updateLanguageLinks(`${yearButton.dataset.yearPageNameOtherLanguage}/${year}/`);

      customCursors.removeElementCursorType(yearButton);
      customCursors.setElementCursorType('point',`${yearContentElementSelector} button,${yearContentElementSelector} a`);

      // Desktop
      if (isDesktop()) {

        const onStart = () => {
          yearButton.classList.remove(CLASS_BOOTSTRAP_OVERFLOW_HIDDEN);
        };

        const onReverseComplete = () => {
          customCursors.setElementCursorType('year',[yearButton]);
          yearButton.classList.add(CLASS_BOOTSTRAP_OVERFLOW_HIDDEN);
          yearContentElement.innerHTML = '';
          // SAMPOTEST-67 Removes style attribute totally because sometimes some styles are left in the dome which blocks scrolling.
          yearContentElement.removeAttribute('style');
          this.getScroller().removeAttribute('data-active-year-page');
        }

        const onComplete = () => {
          document.querySelector('.page-default').classList.add(CLASS_BOOTSTRAP_OVERFLOW_HIDDEN);
          document.querySelector('.page-default').style.height = '100%';
          this.getScroller().classList.add('year-page-active');
          yearScrollerBlock.classList.add('active');
          yearScrollerBlock.style.top = `-${timeline.labels[year]}px`;
        }

        timelineSelectYear = new TimelineMax({
          onStart: onStart,
          onReverseComplete: onReverseComplete,
          paused: animate ? false : true,
          onComplete: onComplete
        });
    
        timelineSelectYear.addLabel('moveToYearContentStarts');

        // Fade out timeline left marker-wrappers
        timelineSelectYear.fromTo(
          '.timeline.left .year.highlight,.timeline.left .year.active',
          {
            opacity: 1
          },
          {
            opacity: 0,
            //left: '-30px', // Remove comments if you want timeline to move outside from the view when year is selected. Without this timeline only fades out. This was removed due to container max-width SAMPOTEST-76.
            duration: 1,
            ease: 'power4.out'
          }
        ).fromTo('.timeline.left .year:not(.highlight)',
          {
            opacity: 1
          },
          {
            opacity: 0,
            //left: '-15px', // Remove comments if you want timeline to move outside from the view when year is selected. Without this timeline only fades out. This was removed due to container max-width SAMPOTEST-76.
            duration: 1,
            ease: 'power4.out'
          },
          '<'
        );

        // Move clicked year
        timelineSelectYear.to(yearScrollerYearElement, 
          {
            duration: 0.7,
            zIndex: 2000,
            right: `${yearNumberPixelsFromRight}px`,
            left: 'initial',
            ease: 'power2.inOut'
          },
          'moveToYearContentStarts'
        );

        timelineSelectYear.addLabel('moveToYearContentEnds');

        // Move left timeline outside the view
        const timelineLeft = document.querySelector('.timeline.left');
        timelineSelectYear.to(timelineLeft,
          {
            opacity: 1,
          },
          {
            opacity: 0,
            duration: 0.3,
            left: '-' + timelineLeft.offsetWidth + 'px',
            ease: 'power1.in'
          },
          '<'
        );

        timelineSelectYear.to(timelineLeft,
          {
            display: 'none'
          },
          '>'
        );

        // Move previous year outside the view
        const previousElementSibling = yearScrollerYearElement.parentElement.parentElement.previousElementSibling;
        if (typeof previousElementSibling !== 'undefined' && previousElementSibling !== null) {
          timelineSelectYear.to(previousElementSibling.firstElementChild.firstElementChild,
            {
              duration: 0.7,
              top: '-40vh',
              ease: 'power2.inOut'
            },
            'moveToYearContentStarts'
          );
        }

        // Move next year outside the view
        const nextYearSibling = yearScrollerYearElement.parentElement.parentElement.nextElementSibling;
        if (typeof nextYearSibling !== 'undefined' && nextYearSibling !== null) {
          timelineSelectYear.to(nextYearSibling.firstElementChild.firstElementChild,
            {
              duration: 0.7,
              top: '40vh',
              ease: 'power2.inOut'
            },
            'moveToYearContentStarts'
          );
        }

        timelineSelectYear.addLabel('nextAndPreviousYearHasMoved');

        // Hide header and footer shades so they are not visible on year page.
        /*timelineSelectYear.fromTo('.background.shade',
          {
            display: 'block'
          },
          {
            display: 'none',
            duration: 0
          },
          'nextAndPreviousYearHasMoved'
        );*/

        // Following animates year content

        // Remove display none from year content wrapper
        timelineSelectYear.fromTo(
          `${yearContentElementSelector},
          .body-header .back-button-wrapper-desktop`,
          {
            display: 'none',
          },
          {
            duration: 0,
            display: 'block'
          },
          '<'
        );


        let maxHeightCalculatorHelper = 0;

        // Year content smaller than year page wrapper -> center content in wrapper
        if (yearPageWrapper.offsetHeight > yearPageWrapper.firstElementChild.offsetHeight && !yearPageWrapper.classList.contains(CLASS_BOOTSTRAP_CENTER_CONTENT)) {
          
          // SAMPOTEST-56: This website doesn't support IE11. However client want's that year page content works.
          // IE11 had broken content with flex centering in year page so we don't add centering class in IE11.
          if (!isBrowser.IE11) { 
            yearPageWrapper.classList.add(CLASS_BOOTSTRAP_CENTER_CONTENT);
          }

        // Year content bigger than year page wrapper -> scrollbar exists so move fixed position elements to show scrollbar
        // 28.5.2020: this is not the right way but first year content should have been centered vertically and now it needs to be scrollable
        } else {

          // 11.6.2020: Now container width is limited to 1600px and this movement is not needed for larger widths SAMPOTEST-66 
          if (window.innerWidth <= CONTAINER_MAX_WIDTH) {
            // No scrollbar under timeline right
            timelineSelectYear.to(
              document.querySelector('.timeline.right'),
              {
                right: getScrollbarWidth(),
                duration: 0
              },
              '<'
            );
            // No shades over scrollbar
            timelineSelectYear.to(
              document.querySelectorAll('.background.shade'),
              {
                width: window.innerWidth - getScrollbarWidth()*2,
                duration: 0
              },
              '<'
            );
          }

          // Show scrollbar
          timelineSelectYear.to(yearContentElement,
            {
              width: '100%',
              duration: 0
            },
            '<'
          );

          maxHeightCalculatorHelper = 15;
        }

        // SAMPOTEST-80: client wants that height of tab element doesn't change when tab is changed.
        const tabContentElement = document.querySelector(`${yearContentElementSelector} .content-column .tabs-content`);
        if (tabContentElement !== null) {
          this.setMaxHeightOfTabContent(tabContentElement,maxHeightCalculatorHelper);
        }

        timelineSelectYear.addLabel('yearContentAnimationStarts','moveToYearContentEnds-=0.5');

        // Fade in year content elements

        const horizontallyMovingElements = document.querySelectorAll(
          `.body-header .back-button-wrapper-desktop,
          ${yearContentElementSelector} .person-wrapper,
          ${yearContentElementSelector} .buttons-wrapper > *`
        );

        if (horizontallyMovingElements.length > 0) {
          timelineSelectYear.fromTo(horizontallyMovingElements,
            {
              left: '70px',
              opacity: 0
            },
            {
              left: '0px',
              opacity: 1,
              duration: 0.3,
              ease: 'power2.out',
              stagger: 0.05
            },
            'yearContentAnimationStarts'
          );
        }

        const verticallyMovingElements = document.querySelectorAll(
          `${yearContentElementSelector} .tabs-navigation .nav-link,
          ${yearContentElementSelector} .tabs-content .tab-pane ul li`
        );
        
        const verticalMovementInPixels = 15;

        if (verticallyMovingElements.length > 0) {
          timelineSelectYear.fromTo(verticallyMovingElements,
            {
              top: `${verticalMovementInPixels}px`,
              opacity: 0
            },
            {
              top: '0px',
              opacity: 1,
              duration: 0.3,
              ease: 'power2.out',
              stagger: 0.05
            },
            '>-0.25'
          );
        }

        timelineSelectYear.addLabel('verticalElementsFadeInStarts','<');
        
        // Fade in borders of tab structure
        const borderInvisible = 'rgba(104,104,104,0)';
        const borderVisible = 'rgba(104,104,104,1)' ;
        const borderLightInvisible = 'rgba(41,38,68,0)';
        const borderLightVisible = 'rgba(41,38,68,0.1)';
        
        // Fade in active tab borders
        const activeTabLink = document.querySelector(`${yearContentElementSelector} .nav-link.active`);
        if (activeTabLink !== null) {
          timelineSelectYear.fromTo(
            activeTabLink,
            {
              borderTopColor: borderInvisible,
              borderLeftColor: borderInvisible,
              borderRightColor: borderInvisible
            },
            {
              borderTopColor: borderVisible,
              borderLeftColor: borderVisible,
              borderRightColor: borderVisible,
              clearProps: 'borderTopColor,borderLeftColor,borderRightColor',
              duration: 0.3
            },
            'verticalElementsFadeInStarts+=0.15'
          );
        }

        // Fade in tabs navigation borders
        const tabsNavigation = document.querySelector(`${yearContentElementSelector} .tabs-navigation`);
        if (tabsNavigation !== null) {
          timelineSelectYear.fromTo(
            tabsNavigation,
            {
              borderBottomColor: borderInvisible,
            },
            {
              borderBottomColor: borderVisible,
              clearProps: 'borderBottomColor',
              duration: 0.3
            },
            'verticalElementsFadeInStarts+=0.15'
          );
        }

        // Fade in tab content links
        const tabContentLinks = document.querySelectorAll(
          `${yearContentElementSelector} .tab-pane ul li a`
        );
        if (tabContentLinks.length > 0) {
          timelineSelectYear.fromTo(
            tabContentLinks,
            {
              borderBottomColor: borderLightInvisible,
            },
            {
              borderBottomColor: borderLightVisible,
              duration: 0.3,
              stagger: 0.05
            },
            'verticalElementsFadeInStarts+=0.20'
          );
        }

        // Sometimes we need to move to year page quickly without animations (for example on resize event). 
        // This checks if we need to skip previously created animations.
        if (!animate) {
          timelineSelectYear.progress(1);
        }
      // Mobile
      } else {

        // This needs to be saved so in back button click we can set same position as at the start of year page.
        const scrollPositionBeforeYearPage = window.scrollY;

        const onStart = () => {
          document.body.classList.add(CLASS_BOOTSTRAP_OVERFLOW_HIDDEN);
          document.querySelector('.page-default').classList.add(CLASS_BOOTSTRAP_OVERFLOW_HIDDEN);
          document.querySelector('.page-default').style.height = '100%';
        }

        const onReverseComplete = () => {
          document.body.classList.remove(CLASS_BOOTSTRAP_OVERFLOW_HIDDEN);
          enableScroll();
          yearScrollerBlock.style.height = 'auto';
          document.querySelector('.page-default').classList.remove(CLASS_BOOTSTRAP_OVERFLOW_HIDDEN);
          document.querySelector('.page-default').style.height = 'auto';
          this.getScroller().removeAttribute('data-active-year-page');
          window.scrollTo(0,scrollPositionBeforeYearPage);
        }

        timelineSelectYear = new TimelineMax({
          onStart: onStart,
          onReverseComplete: onReverseComplete
        });

        timelineSelectYear.to(yearContentElementSelector,
          {
            duration: 0,
            position: 'fixed',
            display: 'block',
            top: '0',
            left: '0',
            height: '100%',
            width: '100%',
            paddingTop: '66px',
            background: 'white',
            opacity: 1
          }
        );
        timelineSelectYear.to('.body-header .back-button-wrapper-mobile .button-back',
          {
            display: 'inline-block'
          },
          '<'
        );

      }
    } // End of onDone callback.

    getMarkupFromBackend({
      data: {
        year,
        showYear: isDesktop() ? false : true
      },
      ajaxUrl: '/stage-ajax/getYearMarkup',
      targetElementSelector: yearContentElementSelector,
      mustacheTemplate: 'YEAR_PAGE',
      onDone: onDone
    });
    
  },

  /**
   * Starts animations on page load (on desktop after preloader). 
   * Checks if we should start from entrance animations or skip straight to year page animation.
   */
  start: function () {

    // Desktop 
    if (isDesktop()) {
      //timelineEntrance.progress(1); // Skips entrance animations totally (helps developing)
      if (this.onLoadYear) {
        // No entrance animation needed set progress to 1 (=end of entrance animation)
        timelineEntrance.progress(1);
        // Set scroll bar to onLoadYear label
        window.scrollTo(0,timeline.labels[this.onLoadYear])
        // Move to onLoadYear page
        this.showYearContent(this.onLoadYear);
      } else {
        timelineEntrance.play();
      }

    // Mobile
    } else {
      //timelineEntrance.progress(1); // Skip entrance timeline in mobile (helps developing)
      // Loaded with year url so show year content
      if (this.onLoadYear) {
        timelineEntrance.progress(1);
        this.showYearContent(this.onLoadYear);
      } else {
        //scrollToTop();
        // If page is at the top play entrance animation
        if (window.scrollY < scrollYInMobileWhereHeroDisappears) {
          scrollToTop();
          document.body.classList.add(CLASS_BOOTSTRAP_OVERFLOW_HIDDEN);
          timelineEntrance.play();
        // Skip entrance if page is not at the top.
        } else {
          timelineEntrance.progress(1);
        }
      }
    }
  },

  /**
   * Removes timeline object completely and inline styles which are used on animations.
   * 
   * @param {Object} givenTimeline GSAP timeline object (https://greensock.com/docs/v3/GSAP/Timeline)
   */
  clearGivenTimeline: function (givenTimeline) {
    const targets = givenTimeline.getChildren();
    givenTimeline.kill();
    for (let i=0; i<targets.length; i++) {
      if (targets[i]._targets !== null) {
        TweenMax.set(targets[i]._targets, {clearProps: 'all'});
      }
    }
  },

  /**
   * Adds year activation animation to given timeline. Updates color in the desktop and also timeline elements
   * on the left and right side of year scroller. Check TimelineModule (./timeline.js) for timeline element functionality.
   * 
   * @param {string} id Year text id which color is changed
   * @param {number} duration Number which is amount of scroll movement in which animation is completed.
   * @param {string} startPosition Animation start position on timeline
   * @param {string} year Year number
   * @param {Object} timelineObject GSAP Timeline object (https://greensock.com/docs/v3/GSAP/Timeline) where
   *                                animation is added.
   */
  addAnimationActivateYear: function (id, duration, startPosition, year, timelineObject) {

    // Updates dom based on active year
    const updateActiveYear = (yearToActivate, yearCurrentlyActive) => {
      // Remove cursor from currently active year button
      const currentActiveYearButton = this.getYearButton(yearCurrentlyActive);
      if (currentActiveYearButton !== null) {
        customCursors.removeElementCursorType(currentActiveYearButton);
      }
      // Add cursor to next active year button
      const nextActiveYearButton = this.getYearButton(yearToActivate);
      if (nextActiveYearButton && timelineEntrance.progress() === 1) {
        customCursors.setElementCursorType('year',[nextActiveYearButton]);
      }
      // Activate correct year on timeline elements on the left and right side of year scroller.
      TimelineModule.updateTimelineYear(yearToActivate);
    }

    // Set params for activation
    const params = {
      duration: duration,
      onStart: updateActiveYear,
      onStartParams: [year, parseInt(year)+1],
      onReverseComplete: updateActiveYear,
      onReverseCompleteParams: [parseInt(year)+1, year],
      data: {
        type: 'activate',
        year: year
      }
    }

    // Add desktop params for animation
    if (isDesktop()) {
      params.color = '#101B2A';
      params.textShadow = 'none';
      params.zIndex = 1031;
    // Add mobile params for animation
    } else {
      params.clearProps = 'all';
    }

    // Add animation to timeline on start position.
    timelineObject.add(
      gsap.to(id,params),
      startPosition
    );
  },

  /**
   * Fades out currently active year on year scroller. Updates color on desktop and 
   * timeline element on the left and right side of year scroller.
   * 
   * @param {string} id Year text id which color is changed
   * @param {number} duration Number which is amount of scroll movement in which animation is completed.
   * @param {string} fadeOutStarts Animation start position on timeline
   * @param {string} year Year number
   * @param {Object} timelineObject GSAP Timeline object (https://greensock.com/docs/v3/GSAP/Timeline) where
   *                                animation is added.
   */
  addAnimationInactivateYear: function (id, duration, fadeOutStarts, year, timelineObject) {

    // Set animation params
    const params = {
      duration: duration,
      data: {
        type: 'inactivate',
        year: year
      }
    }

    // Add desktop params
    if (isDesktop()) {
      params.color = '#FFFFFF';
      // 5.6.2020 Updated shadow color
      params.textShadow = '0 2px 21px rgba(0,0,0,0.38)';
    // Add mobile params
    } else {
      params.clearProps = 'all';
    }

    // Add animation to timeline object to given position.
    timelineObject.add(
      gsap.to(id,params), 
      fadeOutStarts
    );
  },

  /**
   * Creates main timeline which holds main year scrolling functionality on timeline.
   */
  createTimeline: function () {

    const yearElements = document.querySelectorAll('.year-scroller .years .year-has-annual-report');
    const yearScrollerData = document.querySelector('.year-scroller').dataset;

    // Active year changes when scrolled this amount in mobile
    const activateYearDurationOnMobile = Math.floor((document.body.scrollHeight-document.body.clientHeight) / yearScrollerData.amountOfYears);

    timeline = new TimelineMax({
      paused: true,
    });

    // Loop year elements and add animation for those elements.
    yearElements.forEach((item, index) => {
      const year = item.dataset.year;
      const id = '#year-' + year + '-text';
      const duration = isDesktop() ? Math.floor(item.offsetHeight/2) : activateYearDurationOnMobile;

      // Add active year animation
      const activateYearStarts = (index === 0) ? 0 : '>'
      this.addAnimationActivateYear(
        id, 
        (index === 0) ? 0 : duration, 
        activateYearStarts, 
        year, 
        timeline
      );

      timeline.addLabel(year);

      // All but last
      if (isDesktop() && index < yearElements.length-1) {
        // Add inactivation of the year page
        const inactivateYearStarts = '>';
        this.addAnimationInactivateYear(
          id, 
          duration, 
          inactivateYearStarts, 
          year,
          timeline
        );
      }

    });
  },

  /**
   * Create timeline for on page load animations.
   */
  createTimelineEntrance: function () {

    const onComplete = () => {
      enableScroll();
      // When on page load animations are completed add cursor for first active year
      customCursors.setElementCursorType('year',[document.querySelector('.years .year-has-annual-report .year-button')]);
    }

    timelineEntrance = new TimelineMax({
      paused: true,
      onComplete: onComplete
    });

    const heroHeading = document.querySelector('.hero-wrapper .hero-content .heading-1');
    const heroHeadingSecondary = document.querySelector('.hero-wrapper .hero-content .secondary-heading');
    const timelines = TimelineModule.getAllTimelines();

    if (isDesktop()) {

      // Fade in big heading
      timelineEntrance.fromTo(heroHeading, 
        {
          scale: 0.9,
          opacity: 0
        },
        {
          delay: 0.5,
          scale: 1,
          opacity: 1,
          duration: 1,
        }, '>-0.2');
      // Fade in small heading
      timelineEntrance.fromTo(heroHeadingSecondary, 
        {
          opacity: 0
        },
        {
          opacity: 1,
          duration: 1,
        }, 
        '>-0.5'
      );
      // Fade in scroll down button
      timelineEntrance.fromTo('.hero-wrapper .hero-content .button-down-wrapper .button-down', 
        {
          opacity: 0,
          bottom: '20px'
        },
        {
          bottom: '0px',
          opacity: 1,
          duration: 0.7,
          ease: 'power4.out'
        }, 
        '>'
      );

      // Hero is fully visible. This label helps to position following tweens
      timelineEntrance.addLabel('heroFadedIn');
      
      const yearInFuture = document.querySelector('.year-scroller .years .year-in-future');

      // Fade in year in future and remove margin bottom so first year with annual report moves on the screen

      // Set year in future position for different breakpoints.
      let yearInFutureMarginBottom = '39vh';
      let yearInFutureNumberMarginTop = '0vh';

      // SAMPOTEST-36, SAMPOTEST-13, SAMPOTEST-81 etc: 
      // Why there are so many breakpoints? Font size affects to animation and it need's to be perfect in all screen sizes.
      // There might be better way to year in future position but it's hard because font has some white padding in it.
      // White padding increases because of huge font and size of that padding cannot be calculated.

      // THESE ARE WIDTH BREAKPOINTS FOR MEDIUM HEIGHT SCREENS
      // Width extra large height under large
      if (window.innerWidth >= BOOTSTRAP_BREAKPOINT_EXTRA_LARGE && window.innerHeight >= BREAKPOINT_HEIGHT_SMALL && window.innerHeight < BREAKPOINT_HEIGHT_LARGE) {
        yearInFutureNumberMarginTop = '0vh';
      // Width between large and extra large height under large
      } else if (window.innerWidth >= BOOTSTRAP_BREAKPOINT_LARGE && window.innerHeight >= BREAKPOINT_HEIGHT_SMALL && window.innerHeight < BREAKPOINT_HEIGHT_LARGE) {
        yearInFutureNumberMarginTop = '0vh';
      // Width between medium and large height under large
      } else if (window.innerWidth >= BOOTSTRAP_BREAKPOINT_MEDIUM && window.innerHeight >= BREAKPOINT_HEIGHT_MEDIUM && window.innerHeight < BREAKPOINT_HEIGHT_LARGE) {
        //yearInFutureNumberMarginTop = '5.5vh';
        yearInFutureNumberMarginTop = '2vh';

      // THESE ARE WIDTH BREAKPOINTS FOR UNDER MEDIUM HEIGHT SCREENS
      // Width between medium and large, height under medium and over small 
      } else if (
        window.innerWidth >= BOOTSTRAP_BREAKPOINT_MEDIUM && window.innerWidth < BOOTSTRAP_BREAKPOINT_LARGE // Width 992-1200
        && window.innerHeight >= BREAKPOINT_HEIGHT_SMALL && window.innerHeight < BREAKPOINT_HEIGHT_MEDIUM // Height 600-760
      ) {
        yearInFutureNumberMarginTop = '8vh';

        // Tries to target iPad for example iPad 7th
        if (isBrowser.Safari) {
          yearInFutureNumberMarginTop = '8vh';
          yearInFutureMarginBottom = '34vh';
        }

      // THESE ARE WIDTH BREAKPOINTS FOR UNDER SMALL HEIGHT SCREENS
      // Width between medium and large, height under small (for example Samsung Galaxy tab S5e)
      } else if (
        window.innerWidth >= BOOTSTRAP_BREAKPOINT_MEDIUM && window.innerWidth < BOOTSTRAP_BREAKPOINT_LARGE // Width 992-1200
        && window.innerHeight < BREAKPOINT_HEIGHT_SMALL // Height under 600
      ) {
        yearInFutureNumberMarginTop = '7vh';
        yearInFutureMarginBottom = '36vh';

        // Tries to target Android tablets with Samsung browser
        if (isDevice.Android() && isBrowser.Samsung) {
          yearInFutureNumberMarginTop = '8vh';
          yearInFutureMarginBottom = '34vh';
        // Tries to target Android tablets with Chrome browser
        } else if (isDevice.Android() && isBrowser.Chrome) {
          yearInFutureNumberMarginTop = '11vh';
          yearInFutureMarginBottom = '35vh';
        }

      // THESE ARE WIDTH BREAKPOINTS FOR LARGE HEIGHT SCREENS
      // Width extra large 1400-1499
      } else if (window.innerWidth >= BOOTSTRAP_BREAKPOINT_EXTRA_LARGE && window.innerWidth < 1500 && window.innerHeight < BREAKPOINT_HEIGHT_EXTRA_LARGE) {
        yearInFutureNumberMarginTop = '0.3vh';
      // Width large between 1300-1399
      } else if (window.innerWidth >= 1300 && window.innerWidth < BOOTSTRAP_BREAKPOINT_EXTRA_LARGE && window.innerHeight < BREAKPOINT_HEIGHT_EXTRA_LARGE) {
        yearInFutureNumberMarginTop = '2vh';
      // Width large between 1200-1299
      } else if (window.innerWidth >= BOOTSTRAP_BREAKPOINT_LARGE && window.innerWidth < 1300 && window.innerHeight < BREAKPOINT_HEIGHT_EXTRA_LARGE) {
        yearInFutureNumberMarginTop = '3vh';
      // Width medium between 1100-1199
      } else if (window.innerWidth >= 1100 && window.innerWidth < BOOTSTRAP_BREAKPOINT_LARGE && window.innerHeight < BREAKPOINT_HEIGHT_EXTRA_LARGE) {
        yearInFutureNumberMarginTop = '5.5vh';
      // Width medium between 992-1099
      } else if (window.innerWidth >= BOOTSTRAP_BREAKPOINT_MEDIUM && window.innerWidth < 1100 && window.innerHeight < BREAKPOINT_HEIGHT_EXTRA_LARGE) {
        yearInFutureNumberMarginTop = '6.5vh';

      // THESE ARE WIDTH BREAKPOINTS FOR EXTRA LARGE HEIGHT SCREENS
      } else if (window.innerWidth >= BOOTSTRAP_BREAKPOINT_MEDIUM && window.innerHeight >= BREAKPOINT_HEIGHT_EXTRA_LARGE) {
        yearInFutureNumberMarginTop = '12.5vh';
      }

      // SAMPOTEST-7 due to lock of vh we need to lock this value also so entrance animation positioning is good in Android tablets where timeline jumps
      if (isDevice.Android()) {
        yearInFutureNumberMarginTop = convertVhToPixels(yearInFutureNumberMarginTop);
        yearInFutureMarginBottom = convertVhToPixels(yearInFutureMarginBottom);
      }

      timelineEntrance.fromTo(yearInFuture, 
        {
          opacity: 0, 
          marginBottom: '70vh'
        },
        {
          marginBottom: yearInFutureMarginBottom,
          opacity: 1,
          ease: 'power4.out',
          duration: 1
        }, 
        'heroFadedIn-=0.5'
      );
      timelineEntrance.fromTo(yearInFuture.firstElementChild.firstElementChild, 
        {
          marginTop: '-20vh'
        },
        {
          //marginTop: '0vh',
          marginTop: yearInFutureNumberMarginTop,
          ease: 'power4.out',
          duration: 1
        },
        'heroFadedIn-=0.5'
      );
      // Fade in first annual report year
      timelineEntrance.fromTo(yearInFuture.nextElementSibling, 
        {
          opacity: 0
        },
        {
          opacity: 1,
          ease: 'power4.out',
          duration: 1
        }, 
        'heroFadedIn-=0.5'
      );  
      /*
      // SAMPOTEST-85 Client wants header to be visible on page load.
      // Fade in header and footer
      timelineEntrance.fromTo(document.querySelectorAll('.body-header .body-header-content,.body-footer .body-footer-content'), 
        {
          opacity: 0
        },
        {
          opacity: 1,
          duration: 1,
          ease: 'power4.out'
        },
        'heroFadedIn-=0.5'
      );*/

      const heroFadedIn = () => {
        enableScroll();
      }

      // At this point page load animations are done so pause to the position where hero is visible.
      timelineEntrance.addPause('+=0', heroFadedIn);

      if (isDesktop()) {

        // Pull first annual report year to the center of the screen
        timelineEntrance.to(yearInFuture,{
          marginBottom: '0vh',
          duration: 1,
          ease: 'power4.inOut'
        });

        // Fade out hero
        timelineEntrance.fromTo('.year-scroller-block.hero-wrapper',
          {
            scale: 1,
          },
          {
            scale: 0.85,
            opacity: 0,
            duration: 0.5,
            ease: 'power4.in'
          },
          '<0.1'
        );

        // Time when timeline animation starts
        timelineEntrance.addLabel('showTimelinesStart', '-=0.9')

        timelineEntrance.to('.year-scroller-block.hero-wrapper',
          {
            display: 'none'
          },
          '>'
        );

        // Show timeline elements
        timelineEntrance.to(timelines,{
          opacity: 1,
          duration: 0.1,
          ease: 'sine.inOut'
        },'showTimelinesStart');

        // Animate timeline and it's parts
        TimelineModule.animateTimeline('left', 'showTimelinesStart', timelineEntrance);
        TimelineModule.animateTimeline('right', 'showTimelinesStart', timelineEntrance);
      }

    // After entrance animations first year with annual report is in active state on the center of the screen.

    // mobile
    } else {

      // Fade in header, big and small heading
      /*
      // SAMPOTEST-85:  Client wants header to be visible on page load.
      timelineEntrance.fromTo(
        document.querySelectorAll('.body-header .body-header-content,.body-footer .body-footer-content'), 
        {
          opacity: 0
        },
        {
          opacity: 1,
          duration: 0.5,
          //ease: 'power4.out'
        }
      );*/

      // Fade in hero texts
      timelineEntrance.fromTo(
        [heroHeading,heroHeadingSecondary], 
        {
          scale: 0.9,
          opacity: 0
        },
        {
          scale: 1,
          opacity: 1,
          duration: 0.5,
          stagger: 0.2
        },
        '<+0.2'
      );

      timelineEntrance.addLabel('headerAndHeroVisible','<-0.2');

      // Animate visible year elements
      const visibleYearElements = Array.from(document.querySelectorAll('.year-has-annual-report')).filter(item => item.getBoundingClientRect().top < window.innerHeight)
      timelineEntrance.fromTo(
        visibleYearElements, 
        {
          top: 30,
          opacity: 0
        },
        {
          top: 0,
          opacity: 1,
          duration: 0.5,
          stagger: 0.1
        },
        '<+0.2'
      );

      // Animate timeline and it's parts
      TimelineModule.animateTimeline('left', 'headerAndHeroVisible', timelineEntrance);
      TimelineModule.animateTimeline('right', 'headerAndHeroVisible', timelineEntrance);

      // If entrance animations play on page load scrolling is not allowed. But after entrance page should be scrollable.
      const allowBodyScrolling = () => {
        document.body.classList.remove(CLASS_BOOTSTRAP_OVERFLOW_HIDDEN);
      }
      timelineEntrance.call(allowBodyScrolling);

      timelineEntrance.addPause();

      // Fade out hero
      timelineEntrance.fromTo(
        [heroHeading,heroHeadingSecondary],
        {
          scale: 1,
        },
        {
          scale: 0.85,
          opacity: 0,
          duration: 0.25,
          //ease: 'power4.in'
        },
        '>'
      );

      timelineEntrance.addLabel('heroFadedOut');

      // Pull year scroller to the top of the screen (margin top is the space which hero takes when visible)
      timelineEntrance.fromTo(
        document.querySelector('.year-scroller .years'),
        {
          marginTop: 150
        },
        {
          marginTop: 0,
          duration: 0.5
        },
        '<'
      );

      // Centers timeline
      timelineEntrance.fromTo(
        timelines,
        {
          top: 216
        },
        {
          top: TimelineModule.calculatePositionFromTop(timelines[0]),
          duration: 0.5
        },
        '<'
      );

      // Hide hero
      timelineEntrance.fromTo(
        document.querySelectorAll('.hero-wrapper'),
        {
          display: 'block'
        },
        {
          display: 'none'
        },
        'heroFadedOut'
      );

    }
  },

  /**
   * Switch orientation from desktop to mobile. Removes desktop view related data and initializes mobile view
   * and moves animations to the position before orientation change.
   * 
   * @param {Object} beforeOrientationChangeData Data before orientation change occurs.
   */
  switchDesktopToMobile: function (beforeOrientationChangeData) {
    setTimeout(() => {

      // Select year
      if (typeof timelineSelectYear !== 'undefined') {
        this.desktopBackButtonClick();
        timelineSelectYear.progress(0);
        this.clearGivenTimeline(timelineSelectYear);
      }

      // Entrance
      timelineEntrance.progress(0);
      this.clearGivenTimeline(timelineEntrance);
      this.createTimelineEntrance();
      
      // Main timeline
      timeline.progress(0);
      this.clearGivenTimeline(timeline);
      this.createTimeline();

      setTimeout(() => {
        // Set entrance animations to completed state.
        timelineEntrance.progress(1);

        // Scroll timeline to correct position before orientation change
        window.scrollTo(0,timeline.labels[beforeOrientationChangeData.activeYearOnTimeline]);
  
        // Was it year page before orientation?
        if (beforeOrientationChangeData.activeYearPage !== null && beforeOrientationChangeData.yearPageIsVisible) {
          // Move to year page without animations
          this.showYearContent(beforeOrientationChangeData.activeYearPage,false);
        }
      },100);

    },100);
  },

  /**
   * Switch orientation from mobile to desktop. Removes mobile view related data and initializes desktop view
   * and moves animations to the position before orientation change.
   * 
   * @param {Object} beforeOrientationChangeData Data before orientation change occurs.
   */
  switchMobileToDesktop: function (beforeOrientationChangeData) {
    setTimeout(() => {

      // Select year
      if (typeof timelineSelectYear !== 'undefined') {
        timelineSelectYear.progress(0);
        this.clearGivenTimeline(timelineSelectYear);
        document.body.classList.remove(CLASS_BOOTSTRAP_OVERFLOW_HIDDEN);
        document.querySelector('.page-default').classList.remove(CLASS_BOOTSTRAP_OVERFLOW_HIDDEN);
        document.querySelector('.page-default').style.height = 'auto';
      }

      // Entrance
      timelineEntrance.progress(0);
      this.clearGivenTimeline(timelineEntrance);
      this.createTimelineEntrance();
      
      // Main timeline
      timeline.progress(0);
      this.clearGivenTimeline(timeline);
      this.createTimeline();

      setTimeout(() => {
        // Set entrance animations to completed state.
        timelineEntrance.progress(1);

        // Make timelines interactable.
        Array.from(TimelineModule.getAllTimelines()).map(item => item.classList.add(TimelineModule.dom.classes.INTERACTABLE));

        // Scroll timeline to correct position before orientation change
        window.scrollTo(0,timeline.labels[beforeOrientationChangeData.activeYearOnTimeline]);
  
        // Was it year page before orientation?
        if (beforeOrientationChangeData.activeYearPage !== null && beforeOrientationChangeData.yearPageIsVisible) {
          // Move to year page without animations
          this.showYearContent(beforeOrientationChangeData.activeYearPage,false);
        }

        TimelineModule.setPositionOfTimelines();

      },100);

    },100);
  },

  /**
   * Orientation change event functionality (= when tablet is turned from portrait to landscape or thet other way round). 
   * Breakpoint for mobile and desktop view is on 992px so tablet landscape has desktop view and portrait mobile view.
   * On every mobile<->desktop switch we need to calculate animations again.
   * 
   * @param {Object} event orientationchange event object
   */
  orientationchangeEventFunction: function (event) {

    // Related Jira task SAMPOTEST-63

    // After orientationchange functionality
    var afterOrientationChange = () => {
      
      // New orientation
      const newOrientation = {
        height: window.innerHeight,
        width: window.innerWidth
      }

      const activeYearPage = this.getScroller().getAttribute('data-active-year-page');

      const beforeOrientationChangeData = {
        activeYearOnTimeline: activeYearPage === null ? timeline.currentLabel() : activeYearPage,
        activeYearPage: activeYearPage,
        yearPageIsVisible: this.yearPageVisible(), // Is year page
      }

      // Check if swaps from mobile<->desktop styles (=for example when iPad orientation is changed desktop and mobile styles switches)
      
      // Check if new orientation is over desktop breakpoint
      if (newOrientation.width >= BOOTSTRAP_BREAKPOINT_MEDIUM) {
        // Switch mobile to desktop
        this.switchMobileToDesktop(beforeOrientationChangeData);
      // Check if new orientation is under desktop breakpoint
      } else if (newOrientation.width < BOOTSTRAP_BREAKPOINT_MEDIUM) {
        // Switch desktop to mobile
        this.switchDesktopToMobile(beforeOrientationChangeData);
      }

      // Remove one-time resize event.
      window.removeEventListener('resize', afterOrientationChange);
    };
    
    // Add a one-time resize event on orientation change and remove it after that event is executed.
    window.addEventListener('resize', afterOrientationChange);
  },

  /**
   * SAMPOTEST-7: Android browsers thinks that vh should be calculated based on window.innerHeight and it changes on resize. 
   * We can't do resize every time when browser thinks so because scrolling of timeline is painful if we need to calculate
   * new positions of animations on every resize. And resize doesn't occur regularly so it's hard to calculate correct dimensions. 
   * Locking vh height tries to be fix.
   */
  androidVhUnitFix: function() {

    // Check local storage if there are smallest window height already.
    let localStorageHeight = localStorage.getItem(LOCAL_STORAGE_KEY_SMALLEST_HEIGHT);
    let smallestWindowHeight = window.innerHeight;
    // If innerHeight is in local storage use that
    if (typeof localStorageHeight !== 'undefined' && localStorageHeight !== null) {
      smallestWindowHeight = localStorage.getItem(LOCAL_STORAGE_KEY_SMALLEST_HEIGHT);
    // If no innerHeight in local storage set that
    } else {
      localStorage.setItem(LOCAL_STORAGE_KEY_SMALLEST_HEIGHT, window.innerHeight);
    }
    setVhValue(smallestWindowHeight);

    // Get years element
    const yearsElement = document.querySelector('.year-scroller .years');
    const yearInFuture = document.querySelector('.year-scroller .years .year-in-future');
    const heroWrapper = document.querySelector('.year-scroller .hero-wrapper');

    // Set position to relative
    // Function for setting top position when android browser hides url bar
    const setElementsTopPosition = (pixels) => {
      const chromePositionFix = 20; // By this we can adjust Chrome browser years positioning
      yearsElement.style.top = `${pixels+(isBrowser.Samsung ? 0 : chromePositionFix)}px`;
      yearInFuture.style.top = `${pixels*-1}px`;
      heroWrapper.style.marginTop = `${pixels/2}px`;

      // Adjust timeline position only in Android Chrome
      if (isBrowser.Chrome && !isBrowser.Samsung) {
        const leftTimeline = TimelineModule.getAllTimelines('.left')[0];
        const rightTimeline = TimelineModule.getAllTimelines('.right')[0];
        leftTimeline.style.marginTop = `${pixels}px`;
        rightTimeline.style.marginTop = `${pixels}px`;
      }

      window.dispatchEvent(new Event('resize'));
    } 

    // Set top position on page load
    setElementsTopPosition(window.innerHeight - smallestWindowHeight);

    // Resize event function
    const checkIfBrowserResizes = (() => {
      // Detects if browser inner height decreased on resize
      if (smallestWindowHeight > window.innerHeight) {
        smallestWindowHeight = window.innerHeight;
        localStorage.setItem(LOCAL_STORAGE_KEY_SMALLEST_HEIGHT, smallestWindowHeight);
        setVhValue(smallestWindowHeight); // Change vh to smaller innerheight
        this.reinitMainTimeline(); // Reinit main timeline for smallest vh
        setElementsTopPosition(0);
      }
      // Set top position (only in desktop)
      const topWrapperPosition = isDesktop() ? (window.innerHeight - smallestWindowHeight) * 0.75 : 0;
      setElementsTopPosition(topWrapperPosition); // Set years top position
    }).bind(this);

    // Do resize check on every second
    setInterval(checkIfBrowserResizes, 1000);
  },

  /**
   * Initializes year scroller data and adds event listeners
   */
  init: function () {

    this.centerHeaders();

    // Initializes on load year if year number is in the url.
    this.initOnLoadYear();

    // SAMPOREP-35: 13.1.2020 Changed meta title.
    // SAMPOTEST-90: Changed timeline page document title if on timeline page.
    setDocumentTitle(window.LOCALIZATIONS.TIMELINE_PAGE_TITLE);
    setOgProperty('title',window.LOCALIZATIONS.TIMELINE_PAGE_TITLE);

    // Get event related function in variables so we can add or remove event listener functions
    const mouseWheelEventFunction = this.mouseWheelEventFunction.bind(this);
    const scrollEventFunction = this.scrollEventFunction.bind(this);
    window.addEventListener(scrollEvent,scrollEventFunction);
    window.addEventListener(mouseWheelEvent,mouseWheelEventFunction)
    
    // On touch screen listen orientation change
    if (isTouchScreen()) {

      const orientationchangeEventFunction = this.orientationchangeEventFunction.bind(this);
      window.addEventListener('orientationchange', orientationchangeEventFunction);

      if (isDevice.Android()) {
        this.androidVhUnitFix();
      }

    // On no touch screen listen resize
    } else {
      const resizeEventFunction = this.resizeEventFunction.bind(this);
      window.addEventListener('resize',resizeEventFunction);
    }

    // Add touch start events
    const touchStartEvent = this.handleTouchStart.bind(this);
    const touchMoveEvent = this.handleTouchMove.bind(this);
    document.addEventListener('touchstart', touchStartEvent, false);        
    document.addEventListener('touchmove', touchMoveEvent, false);

    // Add onKeydown event
    const onKeydown = this.onKeydown.bind(this);
    document.addEventListener('keydown', onKeydown);

    // Create entrance and main timeline
    this.createTimelineEntrance();
    this.createTimeline();
   
    // Update timeline change
    this.update();

    // Add click event for timeline years
    this.createOnClickEventsForTimelineYears(TimelineModule.getAllTimelineYears());

    // Add click event for year scroller button
    this.createOnClickEventForScrollerYearButtons(this.getYearButtons()); 

    // Add click event for hero scroll down button
    this.createOnClickEventForHeroScrollDownButton(document.querySelector('.hero .button-down'));

    // Add click event for year page back buttons
    this.createOnClickEventForBackButton(document.querySelectorAll('.body-header .button-back'));

    // SAMPOREP-35 create popstate listener functionality to variable
    yearPagePopstateListener = (event) => {

      // If year page go back to timeline page as if it is back button click on year page.
      if (this.yearPageVisible()) {
        this.backButtonClick('/');
      }
    };
  }

}

module.exports = yearScroller;
